import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { Phone, Mail, MapPin, MessageCircle, Menu, X, Lock, Building2 } from 'lucide-react';
import { fetchSiteInfo, whatsappLink } from './portalApi';
import type { SiteInfo } from './portalApi';

const NAV = [
  { to: '/', label: 'Início', end: true },
  { to: '/imoveis', label: 'Imóveis' },
  { to: '/quem-somos', label: 'Quem Somos' },
  { to: '/servicos', label: 'Serviços' },
  { to: '/contato', label: 'Contato' },
];

export default function PortalLayout() {
  const [site, setSite] = useState<SiteInfo | null>(null);
  const [open, setOpen] = useState(false);
  const [cookieOk, setCookieOk] = useState(true);
  const location = useLocation();

  useEffect(() => {
    fetchSiteInfo().then(setSite).catch(() => undefined);
    try {
      setCookieOk(!!localStorage.getItem('cookie-consent'));
    } catch {
      setCookieOk(true);
    }
  }, []);

  function acceptCookies() {
    try {
      localStorage.setItem('cookie-consent', '1');
    } catch {
      /* ignore */
    }
    setCookieOk(true);
  }

  useEffect(() => {
    setOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (site?.faviconUrl) {
      let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = site.faviconUrl;
    }
    if (site?.name) document.title = site.name;
  }, [site?.faviconUrl, site?.name]);

  const wa = whatsappLink(site?.whatsapp, `Olá! Vim pelo site da ${site?.name || 'imobiliária'} e gostaria de mais informações.`);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 text-gray-800">
      {/* Top bar */}
      <div className="bg-primary-700 text-primary-50 text-xs">
        <div className="max-w-7xl mx-auto px-4 py-1.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 overflow-hidden">
            {site?.phone && (
              <a href={`tel:${site.phone}`} className="flex items-center gap-1 hover:text-white whitespace-nowrap">
                <Phone className="w-3.5 h-3.5" /> {site.phone}
              </a>
            )}
            {site?.email && (
              <a href={`mailto:${site.email}`} className="hidden sm:flex items-center gap-1 hover:text-white whitespace-nowrap">
                <Mail className="w-3.5 h-3.5" /> {site.email}
              </a>
            )}
          </div>
          <div className="flex items-center gap-4 whitespace-nowrap">
            {site?.creci && <span className="hidden sm:inline">CRECI {site.creci}</span>}
            <Link to="/login" className="flex items-center gap-1 hover:text-white">
              <Lock className="w-3.5 h-3.5" /> Área restrita
            </Link>
          </div>
        </div>
      </div>

      {/* Header */}
      <header className="sticky top-0 z-30 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 min-h-[80px] py-2 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            {site?.logoUrl ? (
              <img
                src={site.logoUrl}
                alt={site.name}
                style={{ height: site?.sizes?.headerLogoHeight || 48, maxWidth: '70vw' }}
                className="object-contain"
              />
            ) : (
              <span className="flex items-center gap-2 text-xl font-bold text-primary-700">
                <Building2 className="w-7 h-7" /> {site?.name || 'Imobiliária'}
              </span>
            )}
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive ? 'text-primary-700 bg-primary-50' : 'text-gray-600 hover:text-primary-700 hover:bg-gray-50'
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {site?.whatsapp && (
              <a
                href={wa}
                target="_blank"
                rel="noreferrer"
                className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            )}
            <button
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden p-2 text-gray-600"
              aria-label="Menu"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile nav */}
        {open && (
          <nav className="lg:hidden border-t border-gray-100 bg-white px-4 py-2 space-y-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) =>
                  `block px-3 py-2 rounded-lg text-sm font-medium ${
                    isActive ? 'text-primary-700 bg-primary-50' : 'text-gray-700 hover:bg-gray-50'
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
        )}
      </header>

      <main className="flex-1">
        <Outlet context={{ site }} />
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 mt-12">
        <div className="max-w-7xl mx-auto px-4 py-12 grid gap-8 md:grid-cols-3">
          <div>
            {site?.logoUrl ? (
              <img
                src={site.logoUrl}
                alt={site.name}
                style={{ height: site?.sizes?.footerLogoHeight || 48 }}
                className="max-w-full object-contain bg-white/90 rounded-lg p-2"
              />
            ) : (
              <p className="text-xl font-bold text-white">{site?.name || 'Imobiliária'}</p>
            )}
            <p className="mt-4 text-sm leading-relaxed">{site?.name}</p>
            {site?.creci && <p className="mt-1 text-sm">CRECI {site.creci}</p>}
          </div>

          <div>
            <p className="text-white font-semibold mb-3">Contato</p>
            <ul className="space-y-2 text-sm">
              {site?.phone && (
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-primary-400" /> {site.phone}
                </li>
              )}
              {site?.email && (
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-primary-400" /> {site.email}
                </li>
              )}
              {site?.address?.city && (
                <li className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-primary-400 mt-0.5" />
                  <span>
                    {[site.address.street, site.address.number].filter(Boolean).join(', ')}
                    {site.address.neighborhood ? ` - ${site.address.neighborhood}` : ''}
                    <br />
                    {[site.address.city, site.address.state].filter(Boolean).join('/')}
                  </span>
                </li>
              )}
            </ul>
          </div>

          <div>
            <p className="text-white font-semibold mb-3">Navegação</p>
            <ul className="space-y-2 text-sm">
              {NAV.map((n) => (
                <li key={n.to}>
                  <Link to={n.to} className="hover:text-white">
                    {n.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/login" className="hover:text-white">Área restrita</Link>
              </li>
            </ul>
            <p className="text-white font-semibold mt-5 mb-3">Legal</p>
            <ul className="space-y-2 text-sm">
              <li><Link to="/politica-de-privacidade" className="hover:text-white">Política de Privacidade</Link></li>
              <li><Link to="/politica-de-cookies" className="hover:text-white">Política de Cookies</Link></li>
              <li><Link to="/termos-de-uso" className="hover:text-white">Termos de Uso</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 py-4 text-xs text-gray-400 flex flex-col sm:flex-row items-center justify-between gap-2">
            <span>© {new Date().getFullYear()} {site?.name || 'Imobiliária'}. Todos os direitos reservados.</span>
            <div className="flex items-center gap-3">
              <Link to="/politica-de-privacidade" className="hover:text-white">Privacidade</Link>
              <Link to="/politica-de-cookies" className="hover:text-white">Cookies</Link>
              <Link to="/termos-de-uso" className="hover:text-white">Termos</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Banner de consentimento de cookies (LGPD) */}
      {!cookieOk && (
        <div className="fixed bottom-0 inset-x-0 z-50 bg-gray-900 text-gray-100 shadow-2xl">
          <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col sm:flex-row items-center gap-3">
            <p className="text-sm flex-1">
              Usamos cookies para melhorar sua experiência. Ao continuar navegando, você concorda com a nossa{' '}
              <Link to="/politica-de-cookies" className="underline font-medium">Política de Cookies</Link> e{' '}
              <Link to="/politica-de-privacidade" className="underline font-medium">Política de Privacidade</Link>.
            </p>
            <div className="flex items-center gap-2 shrink-0">
              <Link
                to="/politica-de-cookies"
                className="px-4 py-2 text-sm rounded-lg border border-white/30 hover:bg-white/10"
              >
                Saiba mais
              </Link>
              <button onClick={acceptCookies} className="px-5 py-2 text-sm rounded-lg bg-emerald-600 hover:bg-emerald-700 font-semibold">
                Aceitar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Botão flutuante de WhatsApp */}
      {site?.whatsapp && (
        <a
          href={wa}
          target="_blank"
          rel="noreferrer"
          className="fixed bottom-5 right-5 z-40 flex items-center justify-center w-14 h-14 rounded-full bg-emerald-600 text-white shadow-lg hover:bg-emerald-700"
          aria-label="WhatsApp"
        >
          <MessageCircle className="w-7 h-7" />
        </a>
      )}
    </div>
  );
}
